import { createFileRoute, Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { acceptInvitation, lookupInvitation } from "@/lib/auth/invitations.functions";
import { supabase } from "@/integrations/supabase/client";
import { Logo } from "@/components/intel/Logo";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";

export const Route = createFileRoute("/accept-invite")({
  validateSearch: (s: Record<string, unknown>): { token?: string } =>
    typeof s.token === "string" ? { token: s.token } : {},
  head: () => ({ meta: [{ title: "Accept invitation · CARIO INTEL" }] }),
  component: AcceptInvitePage,
});

function AcceptInvitePage() {
  const search = useSearch({ from: "/accept-invite" });
  const navigate = useNavigate();
  const lookup = useServerFn(lookupInvitation);
  const accept = useServerFn(acceptInvitation);

  const [info, setInfo] = useState<{ email: string; role: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!search.token) { setError("Missing invitation token."); setLoading(false); return; }
      try {
        const r = await lookup({ data: { token: search.token } });
        if (!cancelled) setInfo(r);
      } catch (e: any) {
        if (!cancelled) setError(e?.message || "Invalid invitation");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [search.token, lookup]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!info) return;
    setBusy(true);
    try {
      await accept({ data: { token: search.token, password, display_name: displayName || undefined } });
      const { error: signErr } = await supabase.auth.signInWithPassword({ email: info.email, password });
      if (signErr) throw signErr;
      toast.success("Welcome aboard");
      navigate({ to: "/dashboard" });
    } catch (e: any) {
      toast.error(e?.message || "Could not accept invitation");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen grid-bg bg-background text-foreground flex items-center justify-center px-4">
      <Toaster />
      <div className="w-full max-w-md">
        <div className="mb-6 flex items-center justify-between">
          <Link to="/"><Logo /></Link>
          <Link to="/login" className="font-mono text-[10px] uppercase tracking-[0.25em] text-cyan hover:text-foreground">
            Sign in →
          </Link>
        </div>
        <div className="glass-strong rounded-xl p-8 ring-glow">
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-cyan">// Invitation</p>
          <h1 className="mt-2 font-display text-3xl">Activate your access</h1>

          {loading && <p className="mt-6 text-sm text-muted-foreground">Validating invitation…</p>}

          {error && (
            <div className="mt-6 rounded-md border border-destructive/40 bg-destructive/10 p-4 text-sm">
              {error}
            </div>
          )}

          {info && !error && (
            <form onSubmit={handleSubmit} className="mt-6 space-y-4">
              <div className="space-y-1.5">
                <Label>Email</Label>
                <Input value={info.email} disabled />
              </div>
              <div className="space-y-1.5">
                <Label>Role</Label>
                <Input value={info.role} disabled />
              </div>
              <div className="space-y-1.5">
                <Label>Display name (optional)</Label>
                <Input value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label>Choose a password</Label>
                <Input type="password" minLength={8} required value={password}
                  onChange={(e) => setPassword(e.target.value)} />
              </div>
              <Button type="submit" disabled={busy} className="w-full">
                {busy ? "Activating…" : "Activate account"}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}